'use client';

import clsx from 'clsx/lite';
import { motion } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { TeamLogo } from '~/components/layouts/Header';
import type { MenuItem } from '~/types/graphql';

const closeSearch = new Event('search:close');

export function MobileMenu({
  items,
  team,
  allStarCupMenuItems,
}: {
  items: MenuItem[];
  team?: string;
  allStarCupMenuItems: MenuItem[];
}) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  // if first segment is all-star-cup, set active menu to all star cup
  const [activeMenu, setActiveMenu] = useState<
    'Regular Season' | 'All-Star Cup'
  >(
    pathname?.split('/')[1] === 'all-star-cup'
      ? 'All-Star Cup'
      : 'Regular Season',
  );

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const closeMenu = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    window.addEventListener('menu:close', closeMenu);
    return () => {
      window.removeEventListener('menu:close', closeMenu);
    };
  }, []);

  const handleClick = () => {
    window.dispatchEvent(closeSearch);
    setIsOpen(!isOpen);
  };

  const menuItems = activeMenu === 'All-Star Cup' ? allStarCupMenuItems : items;

  return (
    <>
      <button
        className="absolute left-5 flex w-5 flex-col gap-[7px]"
        onClick={handleClick}
        aria-label="Toggle navigation"
        aria-controls="mobile-navigation"
        aria-expanded={isOpen}
      >
        <span
          className={clsx(
            'block h-0.5 w-5 bg-current transition-all duration-200',
            isOpen && 'w-[26px] origin-top-left rotate-45',
          )}
        />
        <span
          className={clsx(
            'block h-0.5 w-5 bg-current transition-all duration-200',
            isOpen && 'w-0 origin-center opacity-0',
          )}
        />
        <span
          className={clsx(
            'block h-0.5 w-5 bg-current transition-all duration-200',
            isOpen && 'w-[26px] origin-bottom-left -rotate-45',
          )}
        />
      </button>

      <motion.nav
        id="mobile-navigation"
        initial={{ height: 0 }}
        animate={{ height: isOpen ? 'auto' : '0' }}
        className="absolute inset-x-0 top-[calc(100%-36px)] z-100 w-auto overflow-hidden bg-[inherit] px-[--site-margin]"
        aria-label="header menu"
      >
        <ul className="flex flex-col gap-y-2.5 pb-5 pt-9 text-[16px] font-medium leading-none">
          <SeasonMenu
            activeMenu={activeMenu}
            pathname={pathname ?? ''}
            setActiveMenu={setActiveMenu}
          />

          {activeMenu !== 'All-Star Cup' && <TeamMenu team={team} />}

          {menuItems.map((item, i) => (
            <MobileMenuItem key={i} item={item} team={team} />
          ))}
        </ul>
      </motion.nav>
    </>
  );
}

function MobileMenuItem({ item, team }: { item: MenuItem; team?: string }) {
  const pathname = usePathname();

  const [isOpen, setIsOpen] = useState(false);

  // close menu on path change
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const handleClick = () => {
    setIsOpen((prev) => !prev);
  };

  if (item.childItems?.nodes?.length) {
    return (
      <li>
        <button
          className={clsx(
            'flex h-[65px] w-full flex-nowrap items-center gap-x-2 rounded-lg bg-white/10 px-4',
            team ? 'text-[--header-text]' : 'text-white',
          )}
          onClick={item.childItems?.nodes.length ? handleClick : undefined}
        >
          {item.label}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="7"
            viewBox="0 0 12 7"
            className={clsx(
              'mb-px stroke-current transition-transform duration-300',
              isOpen && 'rotate-180',
            )}
            fill="none"
          >
            <path
              d="M1.51758 0.979492L6.09794 5.97949L10.6783 0.979492"
              strokeWidth="1.52679"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <ul
          className={clsx(
            'w-full flex-col gap-2.5 pl-8 pt-2.5',
            isOpen ? 'flex' : 'hidden',
          )}
        >
          <Link
            href={item.uri ?? ''}
            className={clsx(
              'flex h-[65px] flex-nowrap items-center gap-x-2 rounded-lg bg-white/10 px-4',
              team ? 'text-[--header-text]' : 'text-white',
            )}
          >
            Overview
          </Link>
          {item.childItems.nodes.map((childItem: MenuItem, i) => (
            <MobileMenuItem key={i} item={childItem} team={team} />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li>
      <Link
        href={item.uri ?? ''}
        className={clsx(
          'flex h-[65px] flex-nowrap items-center gap-x-2 rounded-lg bg-white/10 px-4',
          team ? 'text-[--header-text]' : 'text-white',
        )}
        onClick={item.childItems?.nodes.length ? handleClick : undefined}
      >
        {item.label}
      </Link>
    </li>
  );
}

const teams = ['bandits', 'talons', 'blaze', 'volts'];

function TeamMenu({ team }: { team?: string }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <li>
      <button
        className={clsx(
          'flex h-[65px] w-full flex-nowrap items-center gap-x-2 rounded-lg bg-white/10 px-4 transition-colors duration-300',
          isOpen && (team ? 'bg-black/10' : '!bg-ausl-blue-light'),
        )}
        onClick={handleClick}
      >
        {team ? 'League' : 'Teams'}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          className={clsx(
            'mb-px stroke-current transition-transform duration-300',
            isOpen && 'rotate-180',
          )}
          fill="none"
        >
          <path
            d="M1.51758 0.979492L6.09794 5.97949L10.6783 0.979492"
            strokeWidth="1.52679"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <ul
          className={clsx(
            'w-full flex-col gap-2.5 pl-8 pt-2.5',
            isOpen ? 'flex' : 'hidden',
          )}
        >
          {teams.map((item, i) => {
            if (item === team) return null;

            return (
              <li key={i} className="h-[67px]">
                <TeamLogo team={item} />
              </li>
            );
          })}
          {!!team && (
            <li className="mb-2 h-[43px]">
              <Link
                href="/"
                className="flex h-full w-full items-center justify-center rounded bg-black/10 px-4 font-medium"
              >
                Return to AUSL Homepage
              </Link>
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

function SeasonMenu({
  pathname,
  activeMenu,
  setActiveMenu,
}: {
  pathname: string;
  activeMenu: string;
  setActiveMenu: React.Dispatch<
    React.SetStateAction<'All-Star Cup' | 'Regular Season'>
  >;
}) {
  const inactiveMenu =
    activeMenu === 'All-Star Cup' ? 'Regular Season' : 'All-Star Cup';

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <li>
      <button
        className={clsx(
          'flex h-[65px] w-full flex-nowrap items-center gap-x-2 rounded-lg px-4 transition-colors duration-300',
          'bg-[#091724] text-white',
        )}
        onClick={handleClick}
      >
        {activeMenu}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          className={clsx(
            'mb-px stroke-current transition-transform duration-300',
            isOpen && 'rotate-180',
          )}
          fill="none"
        >
          <path
            d="M1.51758 0.979492L6.09794 5.97949L10.6783 0.979492"
            strokeWidth="1.52679"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className={clsx(
            'w-full flex-col gap-2.5 pl-8 pt-2.5',
            isOpen ? 'flex' : 'hidden',
          )}
        >
          <button
            className={clsx(
              'flex h-[67px] w-full flex-nowrap items-center gap-x-2 whitespace-nowrap rounded-lg px-4 font-normal',
              'bg-[#091724] text-white',
            )}
            onClick={() => setActiveMenu(inactiveMenu)}
          >
            <span className="label">{inactiveMenu}</span>
          </button>
        </div>
      )}
    </li>
  );
}
